import { Link } from 'react-router-dom'
import { Star, MapPin, Clock, ChevronRight, Zap, ShieldCheck, Droplets } from 'lucide-react'
import { CertBadge } from '../ui/CertBadge'
import type { DirectoryPlumberCard as IDirectoryPlumberCard } from '../../types'
import { useDirectoryMode } from '../../hooks/useDirectoryMode'
import { formatPlumberBaseLocation } from '../../lib/plumberLocation'


interface Props {
  plumber: IDirectoryPlumberCard
  href: string
}

export function DirectoryPlumberCard({ plumber, href }: Props) {
  const { isGasSafe, isWaterSafe } = useDirectoryMode()
  const platformRatings = plumber.platformRatings ?? []
  const certifications = plumber.certifications ?? []

  const bestRating = plumber.googleRating
    ?? platformRatings.find(p => p.rating !== undefined)?.rating

  const bestReviewCount = plumber.googleReviewCount
    ?? platformRatings.find(p => p.reviewCount !== undefined)?.reviewCount

  const baseLocation = formatPlumberBaseLocation(plumber)

  return (
    <Link
      to={href}
      className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all p-5 flex gap-4"
    >
      {/* Logo */}
      <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-white border border-gray-200 overflow-hidden flex items-center justify-center">
        {plumber.logoUrl || plumber.photoUrl ? (
          <img
            src={plumber.logoUrl || plumber.photoUrl}
            srcSet={plumber.logoUrl && plumber.logoVariants?.length ? plumber.logoVariants.map(v => `${v.url} ${v.width}w`).join(', ') : undefined}
            sizes="56px"
            alt={plumber.companyName}
            width={56}
            height={56}
            loading="lazy"
            decoding="async"
            className={`w-full h-full ${plumber.logoUrl ? 'object-contain' : 'object-cover'}`}
            onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
          />
        ) : (
          <span className="text-xl font-bold text-gold-500">
            {plumber.companyName.charAt(0).toUpperCase()}
          </span>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 group-hover:text-trust-600 transition-colors truncate">
              {plumber.companyName}
            </h3>
            <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
              <MapPin className="h-3 w-3 flex-shrink-0" />
              {baseLocation || '-'}
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-trust-400 flex-shrink-0 mt-0.5 transition-colors" />
        </div>

        {/* Rating */}
        {bestRating !== undefined && (
          <div className="flex items-center gap-1.5 mt-2">
            <div className="flex">
              {[1,2,3,4,5].map(i => (
                <Star
                  key={i}
                  className={`h-3.5 w-3.5 ${i <= Math.round(bestRating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}`}
                />
              ))}
            </div>
            <span className="text-xs font-medium text-gray-700">{bestRating.toFixed(1)}</span>
            {bestReviewCount !== undefined && (
              <span className="text-xs text-gray-400">({bestReviewCount.toLocaleString()})</span>
            )}
          </div>
        )}

        {/* Tags row */}
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          {isGasSafe && (
            <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded-full">
              <ShieldCheck className="h-2.5 w-2.5" /> Gas Safe
            </span>
          )}
          {isWaterSafe && (
            <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-cyan-700 bg-cyan-50 border border-cyan-100 px-1.5 py-0.5 rounded-full">
              <Droplets className="h-2.5 w-2.5" /> WaterSafe
            </span>
          )}
          {plumber.emergencyAvailable && (
            <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-emergency-600 bg-emergency-50 border border-emergency-100 px-1.5 py-0.5 rounded-full">
              <Zap className="h-2.5 w-2.5" /> 24/7 Emergency
            </span>
          )}
          {plumber.workingHours?.alwaysOpen && !plumber.emergencyAvailable && (
            <span className="inline-flex items-center gap-0.5 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded-full">
              <Clock className="h-2.5 w-2.5" /> Always open
            </span>
          )}
          {(() => {
            const speedLabel: Record<string, string> = {
              '30-min':   '30-min call-out',
              '1-hour':   '1-hour call-out',
              'same-day': 'Same-day',
              'next-day': 'Next-day',
            }
            const label = plumber.responseTimeHint ? speedLabel[plumber.responseTimeHint] : undefined
            return label ? (
              <span className="inline-flex items-center gap-0.5 text-[11px] font-medium text-violet-700 bg-violet-50 border border-violet-100 px-1.5 py-0.5 rounded-full">
                <Clock className="h-2.5 w-2.5" />{label}
              </span>
            ) : null
          })()}
          {certifications.slice(0, 2).map(c => (
            <CertBadge key={c} name={c} size="sm" />
          ))}
        </div>

        {/* Description snippet */}
        {plumber.description && (
          <p className="text-xs text-gray-500 mt-2 line-clamp-2 leading-relaxed">
            {plumber.description}
          </p>
        )}

        {/* Pricing hint */}
        {plumber.pricing?.callOutFeeGbp !== undefined && (
          <p className="text-xs text-gray-500 mt-1">
            Call-out from <span className="font-medium text-gray-700">£{plumber.pricing.callOutFeeGbp}</span>
          </p>
        )}
      </div>
    </Link>
  )
}
